import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { Menu, X } from 'lucide-react';
import Sidebar from './Sidebar';
import SalesProgressBar from '../SalesProgressBar/SalesProgressBar';
import StandbyButton from '../StandbyButton';
import { clearSalesFlowSession } from '../../utils/salesSession';
import { useTranslation } from 'react-i18next';

const LayoutContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background: #f8fafc;
`;

const SidebarWrapper = styled.div`
  position: fixed;
  left: 0;
  top: 0;
  height: 100vh;
  width: 260px;
  z-index: 1000;
  transition: transform 0.3s ease-in-out;
  transform: ${props => props.isOpen ? 'translateX(0)' : 'translateX(-100%)'};
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
  display: ${props => props.isOpen ? 'block' : 'none'};
  
  @media (min-width: 769px) {
    display: none;
  }
`;

const MainContent = styled.main`
  flex: 1;
  margin-left: ${props => props.$sidebarOpen ? '260px' : '0'};
  transition: margin-left 0.3s ease-in-out;
  display: flex;
  flex-direction: column;
  min-height: 100vh;

  @media (max-width: 768px) {
    margin-left: 0;
  }
`;

/** Keeps page title + sales step bar pinned together while scrolling */
const StickySalesHeader = styled.div`
  position: sticky;
  top: 0;
  z-index: 100;
  background: #f8fafc;
`;

const TopBar = styled.header`
  background: white;
  border-bottom: 1px solid #e5e7eb;
  padding: 0.75rem 1.25rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  box-shadow: none;
`;

const MenuButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: none;
  background: #f3f4f6;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;

  &:hover {
    background: #e5e7eb;
  }
`;

const PageTitle = styled.h1`
  font-size: 1.5rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
`;

const ContentArea = styled.div`
  flex: 1;
  padding: 2rem;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

// Sidebar open/collapsed is a user preference, not just a mobile-drawer
// state — tablets (the shop's actual hardware) land in the same >=769px
// bucket as desktop and used to get the sidebar pinned open with no way to
// retract it, permanently eating ~260px that the Rx tables badly need.
const SIDEBAR_OPEN_KEY = 'optiva.sidebarOpen';

const SalesLayout = ({ children, pageTitle = 'Dashboard' }) => {
  const { t } = useTranslation();
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    try {
      const stored = localStorage.getItem(SIDEBAR_OPEN_KEY);
      if (stored !== null) return stored === 'true';
    } catch { /* ignore */ }
    return typeof window === 'undefined' || window.innerWidth > 768;
  });
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);

  useEffect(() => {
    // Get user from localStorage
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        setUser(JSON.parse(userData));
      } catch (e) {
        console.error('Error parsing user data:', e);
      }
    }
  }, []);

  useEffect(() => {
    try { localStorage.setItem(SIDEBAR_OPEN_KEY, String(sidebarOpen)); } catch { /* ignore */ }
  }, [sidebarOpen]);

  useEffect(() => {
    // Close the drawer after navigating, but only on phone-width screens
    // where the sidebar floats over the content — on tablet/desktop the
    // collapsed/expanded choice is a standing preference, not per-page.
    if (typeof window !== 'undefined' && window.innerWidth <= 768) {
      setSidebarOpen(false);
    }
  }, [location.pathname]);

  const handleLogout = () => {
    // Clear the in-progress sales flow so the next user starts clean
    clearSalesFlowSession();
    // Also clear per-user cached identity so it can't leak to the next login
    ['token', 'user', 'user_name', 'user_role', 'storeInfo', 'adminActingStore'].forEach(k => localStorage.removeItem(k));
    navigate('/');
  };

  if (!user) {
    return null; // Wait for user data
  }

  return (
    <LayoutContainer>
      <Overlay isOpen={sidebarOpen} onClick={() => setSidebarOpen(false)} />
      <SidebarWrapper isOpen={sidebarOpen}>
        <Sidebar user={user} onLogout={handleLogout} />
      </SidebarWrapper>
      <MainContent $sidebarOpen={sidebarOpen}>
        <StickySalesHeader>
          <TopBar>
            <MenuButton
              onClick={() => setSidebarOpen(!sidebarOpen)}
              aria-label={t('salesLayout.toggleMenu')}
              title={t('salesLayout.toggleMenu')}
            >
              {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
            </MenuButton>
            <PageTitle>{pageTitle}</PageTitle>
            <div style={{ marginLeft: 'auto' }}>
              <StandbyButton />
            </div>
          </TopBar>
          <SalesProgressBar />
          {/* Pages can pin their own alert here, under the step bar (the
              prescription page uses it for the progressive notice). */}
          <div id="sales-sticky-slot" />
        </StickySalesHeader>
        <ContentArea>
          {children}
        </ContentArea>
      </MainContent>
    </LayoutContainer>
  );
};

export default SalesLayout;

